/**
 * The tinted band at the foot of `RunIdentityCard`: the two CONNECTION settings that govern every
 * run of this sync, under a caps label that says exactly that.
 *
 * ── WHY IT EXISTS, AND WHY IT IS NOT A REGRESSION ───────────────────────────
 * `Sync mode: Mirror` used to sit in the run card's FOOTER, beside `Type:` — a per-run property —
 * with nothing on screen distinguishing the two scopes. It was removed for that reason, and the note
 * left behind said it must not be re-homed onto a per-run pane. Figma `334:514` (band `539:1005`)
 * answers that objection rather than ignoring it: the settings come back, but inside their own
 * tinted, top-bordered band whose first line is the scope itself — `CONNECTION · APPLIES TO EVERY
 * RUN`. The original complaint was about an unlabelled scope, not about the facts being on the page,
 * so labelling the scope is a real answer to it. The band is the label; do not restyle it into the
 * card body, which would reinstate exactly the ambiguity it was built to remove.
 *
 * ── RETENTION MOVED HERE; SO DID ITS SECOND CASE ────────────────────────────
 * `RunsPanel`'s toolbar used to state retention beside the range picker. The frame's toolbar carries
 * no such sentence, so the fact moves here — and with it the case that made the sentence two
 * sentences: `retentionDays === null` is the connection having turned run-log retention OFF
 * (`retentionDays()` in `lib/run-view.ts`), NOT a missing value. It is the one thing on this band
 * that is easy to get wrong and expensive to get wrong: printing the platform default there asserts
 * thirty days of history for a connection that keeps none. `null` renders as `Not retained`, which
 * is a state, not a number.
 *
 * `Edit` opens the sync's own edit sheet — the same surface the page's ⋮ menu opens, and the one
 * that actually owns Log retention (`audiences/components/ActivationSyncSchedule.tsx`'s
 * `activation-log-retention-days`). It is a callback rather than a route because the sheet is state
 * on `ActivationDetail`, not a page of its own.
 *
 * ── THE IDENTIFIER MAPPING, READ-ONLY (Figma study `590:435`) ───────────────
 * The band's last row is the sync's VDM: `email → EMAIL`, one `dt` with N `dd`s. Five decisions came
 * with it, and each is load-bearing.
 *
 * 1. **THE RULE — it shares `Edit`'s gate, exactly** (`594:445`). Both need Lookout to be able to
 *    DESCRIBE this sync; a sync it cannot describe has no mapping to read and no sheet to open. So the
 *    block is rendered off `onEdit` being present rather than off a second condition of its own, and
 *    there is no state where one appears without the other.
 *
 *    WHAT THIS DOES NOT CLAIM: that the gate is currently CLOSED anywhere. `ActivationDetail` passes
 *    `onEditConnection` unconditionally today, so `Edit` — and now the block — render for every sync
 *    the pane can open, including one Lookout holds no `audience_activations` row for. That is a
 *    pre-existing property of the `Edit` link, not something the block introduced, and it was left
 *    alone deliberately rather than tightened in passing. What this file guarantees is the TIE: the
 *    day a caller does gate `Edit`, the block goes with it, with no second place to fix.
 *
 * 2. **The WIRE VALUE, never the human label** (`594:446`). `EMAIL`, not `Email`. Two reasons. The
 *    wire value is the only half that is a STORED FACT — the label is fetched per destination from
 *    integrations-info at render time, and that call really can fail (a 200 carrying `success:false`
 *    is raised as a synthetic 502 in `src/lib/rudderstack-integrations-info.ts`, and a real upstream
 *    5xx maps to INTERNAL_SERVER_ERROR in `src/rpc/procedures/audience.ts`), after which
 *    `ActivationIdentifierMapper`'s option list is empty and the combobox falls back to its
 *    placeholder. A label-keyed block would render nothing on precisely the connections most worth
 *    inspecting. And the edit sheet already shows the label and never the wire value, so the two
 *    surfaces are different projections of the same rows rather than one being a worse copy of the
 *    other. (Verified caveat: LinkedIn's field labels are bundled client-side constants, not a fetch —
 *    it is the one destination whose labels would always resolve. The rule does not turn on it.)
 *
 * 3. **`font-mono` IS the read-only affordance.** Nothing editable in this app is mono, and the phase
 *    card beside this one already carries its wire sub-lines (`nulls 0 · duplicates 0`) in the same
 *    face at the same 11px. The direction is the literal `→` (U+2192) in that mono face, recessive —
 *    NOT the 18px lucide `ArrowRight` the page header uses. `RunsPanel`'s `Changed → Accepted` header
 *    already spells the character out at a small size, so this is the existing convention, not a new one.
 *
 * 4. **Not a third `Setting` row.** A `Setting` is ONE scalar on ONE line; a mapping is 1..N pairs.
 *    Folding N into one line (`email → EMAIL +2 more`) was drawn and REJECTED (`592:1000`): the rows
 *    ARE the content, so a collapsed line answers "is there a mapping" while withholding "is it the
 *    right one", which is the only question worth asking. Gluing the vendor label on as a trailing
 *    gloss was drawn and rejected too (`592:1032`). The block keeps the `Setting` grid — 96px label,
 *    8px gap, the value column — and changes the COMPONENT, not the position. It sits LAST so that
 *    expanding it never moves the two rows above it.
 *
 * 5. **Three absent states, all distinct** (`594:447`). In flight → exactly ONE skeleton bar, never N,
 *    because N bars would assert a row count that is not yet known. That first state is currently
 *    DEFENSIVE rather than reachable through `ActivationDetail`: the pane returns early while
 *    `detail.sync` is undefined, so the props always arrive settled there. It is the correct rendering
 *    of the prop contract (`undefined` = not known), it costs nothing, and it is the state that shows
 *    up if a future caller mounts this band before its sync lands. Zero rows on the wire →
 *    `Not recorded`, never `None` and never `0`: the sync IS keyed on something and Lookout merely
 *    cannot read it, so `None` would be a false statement about a connection that is delivering.
 *    Lookout cannot describe the sync at all → nothing renders, together with `Edit`, per THE RULE.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { cn } from '@/_shared/lib/utils';

import type { IdentifierMapping } from '../types';

interface ConnectionSettingsBandProps {
  /**
   * `sync.config.destination.syncMode` — the platform's own string (`mirror`, `upsert`, …), NOT a
   * value this app narrows. Title-cased for display only; an absent one renders `—` rather than
   * being suppressed, so the band keeps its shape and the reader can see the setting is unanswered
   * instead of wondering whether it exists.
   */
  syncMode?: string | undefined;
  /**
   * This connection's own retention, already resolved by `retentionDays(sync)`. `null` is a real,
   * distinct value — retention turned OFF — and must never be back-filled with the platform default
   * here. See this file's header.
   */
  retentionDays: number | null;
  /**
   * `sync.config.destination.identifierMappings` — the sync's identity mapping, warehouse column to
   * vendor WIRE VALUE.
   *
   * Three values, three meanings, and they must not be collapsed: `undefined` is "the sync has not
   * arrived yet" (the server always emits the array, so this cannot mean an empty config), `[]` is
   * "the connection carries no readable rows", and a non-empty array is the mapping itself.
   */
  identifierMappings?: IdentifierMapping[] | undefined;
  /**
   * `sync.config.destination.fieldMappings` — the second table of LinkedIn (`Attributes`) or Gainsight
   * PX (`Traits`), labelled by `fieldMappingsLabel`. Empty on every other destination type, and an empty one renders no row at all: there is no
   * `Not recorded` state, because a destination with no second table is not missing one.
   */
  fieldMappings?: IdentifierMapping[] | undefined;
  /** What the second table is called for this destination (`Traits`); defaults to `Attributes`. */
  fieldMappingsLabel?: string | undefined;
  /** Opens the sync's edit sheet. Omitted where there is nothing to open — the row then has no link. */
  onEdit?: (() => void) | undefined;
  /**
   * Why `Edit` cannot be used now — present renders it disabled with this on hover. A separate prop
   * rather than a dropped `onEdit`, because THE RULE would take the mapping rows with it.
   */
  editDisabledReason?: string | undefined;
}

/**
 * Above this, every row renders; at or above it, four rows render plus a `Show all N` toggle.
 *
 * A WEIGHT rule, not a data rule — the study's own argument (`594:447`) is that past four rows the
 * connection's config out-weighs the run the page is about, and the count therefore lives in the
 * TOGGLE so "how many identifiers does this sync send" stays answerable without expanding.
 *
 * MEASURED, in Chrome at 1440x900 with the sidebar expanded, on the real detail pane: four rows plus
 * the toggle put the band at 187.5px, and all six expanded put it at 227.5px. (The study's own frames
 * say 186 and 226; the ~1.5px is the value column resolving to 234px here against the frame's 236.)
 */
const MAPPING_ROWS_BEFORE_COLLAPSE = 4;

/** Display-only. The platform owns the vocabulary; this capitalises it and asserts nothing else. */
function syncModeLabel(syncMode: string | undefined): string {
  if (syncMode === undefined || syncMode === '') return '—';
  return syncMode.charAt(0).toUpperCase() + syncMode.slice(1);
}

function Setting({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    // `dt`/`dd` rather than two spans: these are label/value pairs and the association is what lets a
    // screen reader read "Log retention, 30 days" instead of four loose words. The pair is a flex row
    // inside the `<dl>`, which is why `dt` carries the width and `dd` the `min-w-0` — a `<dd>` also
    // ships a browser default `margin-inline-start` that `ml-0` has to cancel.
    <div className="flex items-start gap-2">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="ml-0 min-w-0 flex-1 truncate font-medium text-foreground">{value}</dd>
    </div>
  );
}

/**
 * One mapping table: a single `dt` and N `dd`s, laid out on the `Setting` grid.
 *
 * CSS grid rather than the flex row `Setting` uses, because the shape is genuinely different — a
 * `<dd>` per pair is what makes a screen reader read N values for one term instead of one run-on
 * string, and N flex siblings cannot sit under one 96px label without a wrapper that would put them
 * all back inside a single `<dd>`. `col-start-2` is what pins every `dd` (and the toggle) into the
 * value column; without it grid auto-placement would drop the second row under the LABEL.
 */
function MappingRows({
  label,
  rows,
  emptyLabel,
}: {
  label: string;
  /** `undefined` = not known yet (skeleton). `[]` = known to be empty (`emptyLabel`). */
  rows: IdentifierMapping[] | undefined;
  /** Rendered for `[]`. Omitted where an empty table means "this destination has none", not "unread". */
  emptyLabel?: string;
}): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);

  // A table that is known to be EMPTY and has no word for that is not a row at all — see the
  // `Attributes` call site. A table that is not known yet still renders (its skeleton).
  if (rows?.length === 0 && emptyLabel === undefined) return null;

  const collapsible = rows !== undefined && rows.length > MAPPING_ROWS_BEFORE_COLLAPSE;
  const visible = collapsible && !expanded ? rows.slice(0, MAPPING_ROWS_BEFORE_COLLAPSE) : rows;

  return (
    // `gap-y-1` (4px) between stacked pairs, `gap-x-2` (8px) to the label — the frame's own figures
    // (`592:440`: rows on a 20px pitch over a 16px line box). `items-start` keeps the label on the
    // first pair's baseline rather than centring it against the whole column.
    <div className="grid grid-cols-[6rem_1fr] items-start gap-x-2 gap-y-1">
      <dt className="text-muted-foreground">{label}</dt>

      {visible === undefined ? (
        // ONE bar, never N — see this file's header, point 5. `bg-grey-100` rather than the
        // primitive's default `bg-grey-50`, which IS this band's own `bg-muted` value: the stock
        // skeleton would be invisible here, not merely off-spec.
        <dd className="col-start-2 m-0">
          <Skeleton
            role="status"
            aria-label={`Loading ${label.toLowerCase()}`}
            className="h-[0.6875rem] w-[8.25rem] rounded-[0.1875rem] bg-grey-100"
          />
        </dd>
      ) : visible.length === 0 ? (
        <dd className="col-start-2 m-0 text-muted-foreground">{emptyLabel}</dd>
      ) : (
        visible.map((row) => (
          <dd
            key={mappingKey(row)}
            className="col-start-2 m-0 flex min-w-0 items-center gap-1 font-mono text-[0.6875rem] leading-4"
          >
            {/* The SOURCE column truncates and nothing else does (`594:447`). Losing the tail of a
                warehouse column leaves it recognisable; losing the wire value loses the thing being
                checked. `title` so the whole name is still reachable.

                MEASURED, same session as the figure above, on the study's own reference row: the
                source `customer_email_address_sha256_hashed` wants 238.4px and gets 186.25 (its
                `scrollWidth` 238 against a `clientWidth` 186 — a real ellipsis, not a class that
                merely looks right), while `→ EMAIL` renders whole. The study says 291px into 236 for
                the same row; ours is 286px into 234, and the difference is the FACE — the study is
                set in Roboto Mono, the app in the system mono stack. */}
            <span className="min-w-0 truncate text-foreground" title={row.from}>
              {row.from}
            </span>
            {/* The literal character, not the 18px lucide arrow — see this file's header, point 3. */}
            <span className="shrink-0 text-grey-400">→</span>
            <span className="shrink-0 text-foreground">{row.to}</span>
          </dd>
        ))
      )}

      {collapsible ? (
        <dd className="col-start-2 m-0">
          {/* GREY, not blue. Blue in this card means the click LEAVES it (`Edit`); this one expands in
              place, so it must not borrow the affordance that promises a departure. */}
          <Button
            type="button"
            variant="text"
            className="text-[0.6875rem] leading-4 font-normal text-grey-600 hover:text-grey-800"
            label={expanded ? 'Show fewer' : `Show all ${rows.length}`}
            aria-expanded={expanded}
            onClick={() => {
              setExpanded((open) => !open);
            }}
          />
        </dd>
      ) : null}
    </div>
  );
}

export function ConnectionSettingsBand({
  syncMode,
  retentionDays,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel = 'Attributes',
  onEdit,
  editDisabledReason,
}: ConnectionSettingsBandProps): React.ReactElement {
  const scopeId = useId();

  return (
    // `rounded-b-xl` rather than `overflow-hidden` on the card above: the card's radius has to be
    // honoured at this band's two bottom corners, and clipping the CARD would clip the 2px focus ring
    // the `Edit` button draws (`.claude/skills/ui/SKILL.md` rule 4) at the moment a keyboard user
    // needs to see it. Matching the primitive's own `rounded-xl` keeps the two in step.
    <div
      // `role="group"` + `aria-labelledby`, so the scope label actually LABELS something rather than
      // merely sitting above it. A `<dl>` has no implicit ARIA role (verified — `getByRole('list')`
      // does not find one), so labelling the list itself announced nothing; the group is what carries
      // `CONNECTION` into the accessibility tree, which is the one place the band's scope could
      // otherwise be lost. `dt`/`dd` keep their own native pairing inside it.
      role="group"
      aria-labelledby={scopeId}
      className="flex flex-col gap-1.5 rounded-b-xl border-t border-border bg-muted px-3.5 py-3"
    >
      <div className="flex items-center gap-2">
        {/* The scope IS the band's reason to exist — see this file's header. 11px, matching the
            phase card's own smallest ramp step rather than introducing a fourth size to this pane.

            A `<p>` carrying the `<dl>`'s label, NOT an `<h3>`. This pane has exactly ONE heading by
            design — `RunIdentityCard`'s `<h2>`, which is the accessibility tie back to the table row
            that opened it, and `RunOverview.test.tsx` asserts the count. A second heading would put a
            connection-scoped node into the run's own outline, which is the scope confusion this band
            exists to remove, one level up. `aria-labelledby` gives the settings list the same label
            without adding to the outline. */}
        <p
          id={scopeId}
          className="min-w-0 flex-1 truncate text-[0.6875rem] font-semibold tracking-wider text-muted-foreground uppercase"
        >
          Connection
        </p>
        {onEdit ? (
          <EditConnectionButton onEdit={onEdit} disabledReason={editDisabledReason} className="shrink-0" />
        ) : null}
      </div>

      <dl className="flex flex-col gap-1.5 text-xs">
        <Setting label="Sync mode" value={syncModeLabel(syncMode)} />
        <Setting label="Log retention" value={retentionLabel(retentionDays)} />

        {/* LAST, and only when `Edit` is here — THE RULE, this file's header point 1. Last so that
            expanding `Show all N` never moves the two rows above it. */}
        {onEdit ? (
          <>
            <MappingRows label="Identifiers" rows={identifierMappings} emptyLabel="Not recorded" />
            {/* No `emptyLabel`, and `?? []` rather than the raw prop: only LinkedIn and Gainsight PX have
                a second table, so an empty one means "this destination has none", not "Lookout
                could not read it" — and an unloaded one must not draw a skeleton for a row that will
                most likely never exist. Frame `592:929` draws the in-flight state with `Identifiers`
                alone for exactly that reason. */}
            <MappingRows label={fieldMappingsLabel} rows={fieldMappings ?? []} />
          </>
        ) : null}
      </dl>
    </div>
  );
}

/** How many mapping rows the inline band prints before it names the rest as `+N more`. */
const INLINE_MAPPINGS = 2;

/** A preview row up to this long (source + destination characters) wraps in a narrow strip instead of being cut. */
const INLINE_MAPPING_CHARS = 40;

/**
 * Whether any preview row inside `listRef` is cut by its ellipsis: measured (`scrollWidth >
 * clientWidth`), not guessed from a character count, because a row truncates by WIDTH. Re-measured
 * when the list resizes (the runs pane is resizable) and once the fonts settle.
 */
function usePreviewRowCut(
  listRef: React.RefObject<HTMLElement | null>,
  rows: IdentifierMapping[] | undefined,
): boolean {
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) {
      setCut(false);
      return undefined;
    }
    const measure = (): void => {
      setCut(Array.from(list.children).some((row) => row.scrollWidth > row.clientWidth + 1));
    };
    measure();
    // Measured a frame later, not in the callback: the `Show all` it adds sits beside the list and can
    // narrow it, and a size change during delivery raises a ResizeObserver loop error on window.
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(list);
    void document.fonts?.ready.then(measure);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [listRef, rows]);
  return cut;
}

/**
 * A mapping row's React key: the PAIR, since neither `from` (two vendor fields may read one column) nor
 * `to` is unique alone. `\u0000` as the separator, ESCAPED rather than typed: a raw NUL makes the file
 * invisible to grep (`src/source-hygiene.test.ts`), and no warehouse column or vendor field can contain
 * one, so two pairs never collide the way `from + '-' + to` could.
 */
function mappingKey(row: IdentifierMapping): string {
  return `${row.from}\u0000${row.to}`;
}

/** `null` is OFF, not unknown — never the platform default. See this file's header. */
function retentionLabel(retentionDays: number | null): string {
  return retentionDays === null ? 'Not retained' : `${retentionDays} days`;
}

/** One `label value` pair of the inline band. Wraps as a unit before it wraps inside itself. */
function InlineSetting({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0 font-medium break-words text-foreground">{children}</dd>
    </div>
  );
}

/** A mapping as the bands print it: mono `from → to`, the arrow decorative and spoken as "to". */
function MappingText({ row }: { row: IdentifierMapping }): React.ReactElement {
  return (
    <>
      {/* `anywhere`: a warehouse column has no spaces to break at, and must not widen the strip. */}
      <span className="[overflow-wrap:anywhere]">{row.from}</span>
      <span aria-hidden className="text-grey-400">
        {' → '}
      </span>
      <span className="sr-only"> to </span>
      <span className="[overflow-wrap:anywhere]">{row.to}</span>
    </>
  );
}

/**
 * The inline mapping list: the first {@link INLINE_MAPPINGS} rows in the same mono `from → to` the
 * stacked band prints, then a real `+N more` BUTTON that opens a `Popover` with every row. A button,
 * not hover text: a `title` reaches neither a touch screen nor a keyboard. The popover's list scrolls
 * past about ten rows and is focusable so a keyboard can scroll it.
 */
function InlineMappings({
  label,
  rows,
  emptyLabel,
}: {
  label: string;
  rows: IdentifierMapping[] | undefined;
  emptyLabel?: string;
}): React.ReactElement | null {
  const previewRef = useRef<HTMLSpanElement>(null);
  const previewCut = usePreviewRowCut(previewRef, rows);
  if (rows?.length === 0 && emptyLabel === undefined) return null;
  if (rows === undefined) {
    return (
      <InlineSetting label={label}>
        <Skeleton
          role="status"
          aria-label={`Loading ${label.toLowerCase()}`}
          className="inline-block h-[0.6875rem] w-[8.25rem] max-w-full rounded-[0.1875rem] bg-grey-100 align-middle"
        />
      </InlineSetting>
    );
  }
  if (rows.length === 0) {
    return (
      <InlineSetting label={label}>
        <span className="font-normal text-muted-foreground">{emptyLabel}</span>
      </InlineSetting>
    );
  }
  const shown = rows.slice(0, INLINE_MAPPINGS);
  const restCount = rows.length - shown.length;
  return (
    <InlineSetting label={label}>
      {/* One line per preview row, cut with an ellipsis: the strip sits above the run table, and two
          very long warehouse columns would otherwise take five lines from it. The full rows are one
          click away in the popover below, which opens whenever a preview row is actually cut, even
          with nothing past the first two, so no part of a mapping is reachable only by hover. In a
          strip under 26rem (a phone, or the runs at their minimum beside the chat) a SHORT row wraps
          instead, to two lines at most at 40 characters, rather than hiding its tail. */}
      <span ref={previewRef} className="flex min-w-0 flex-col font-mono text-[0.6875rem] leading-4 font-normal">
        {shown.map((row, index) => (
          <span
            key={mappingKey(row)}
            className={cn(
              'truncate',
              row.from.length + row.to.length <= INLINE_MAPPING_CHARS &&
                '@max-[26rem]:overflow-visible @max-[26rem]:whitespace-normal',
            )}
            title={`${row.from} → ${row.to}`}
          >
            <MappingText row={row} />
            {index < shown.length - 1 ? ',' : null}
          </span>
        ))}
      </span>
      {restCount > 0 || previewCut ? (
        <Popover>
          <PopoverTrigger asChild>
            {/* Grey like the stacked band's `Show all N`: it opens in place, it does not leave the card. */}
            <Button
              type="button"
              variant="text"
              className="ml-1 align-baseline text-[0.6875rem] leading-4 font-normal text-grey-600 hover:text-grey-800 hover:underline"
              label={restCount > 0 ? `+${restCount} more` : 'Show all'}
              // The name starts with the visible text (WCAG 2.5.3), so a voice command that reads the
              // button out ("click plus 23 more") finds it.
              aria-label={
                restCount > 0
                  ? `+${restCount} more ${label.toLowerCase()}, ${rows.length} in all`
                  : `Show all ${rows.length} ${label.toLowerCase()}`
              }
            />
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] p-0">
            <p className="border-b border-border px-3 py-2 text-xs font-semibold text-foreground">
              {label} <span className="font-normal text-muted-foreground">· {rows.length}</span>
            </p>
            {/* ~10 rows (1rem lines + 0.25rem gaps) before it scrolls. */}
            <ul
              aria-label={`All ${label.toLowerCase()}`}
              tabIndex={0}
              className="m-0 flex max-h-[13.5rem] list-none flex-col gap-1 overflow-y-auto px-3 py-2 font-mono text-[0.6875rem] leading-4 text-foreground"
            >
              {rows.map((row) => (
                <li key={mappingKey(row)}>
                  <MappingText row={row} />
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : null}
    </InlineSetting>
  );
}

/**
 * The same band on one line, for `RunStrip` above the run table: the scope label, the settings and
 * `Edit` on a line that WRAPS (never truncates) as the runs pane narrows, and each mapping cut to its
 * first {@link INLINE_MAPPINGS} rows, then a `+N more` button that opens every row in a popover. Same
 * props as {@link ConnectionSettingsBand}, and THE RULE (mappings only beside `Edit`) holds here too.
 */
export function ConnectionSettingsLine({
  syncMode,
  retentionDays,
  identifierMappings,
  fieldMappings,
  fieldMappingsLabel = 'Attributes',
  onEdit,
  editDisabledReason,
}: ConnectionSettingsBandProps): React.ReactElement {
  const scopeId = useId();
  return (
    // The stacked band's own surface (tint, hairline, bottom radius), so the strip reads as the same
    // card family; only the arrangement inside it changes.
    <div
      role="group"
      aria-labelledby={scopeId}
      data-slot="connection-settings"
      data-layout="inline"
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-b-xl border-t border-border bg-muted px-2.5 py-2 text-xs @[26rem]:px-3.5"
    >
      <p
        id={scopeId}
        className="shrink-0 text-[0.6875rem] font-semibold tracking-wider text-muted-foreground uppercase"
      >
        Connection
      </p>
      <dl className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-4 gap-y-1">
        <InlineSetting label="Sync mode">{syncModeLabel(syncMode)}</InlineSetting>
        <InlineSetting label="Log retention">{retentionLabel(retentionDays)}</InlineSetting>
        {onEdit ? (
          <>
            <InlineMappings label="Identifiers" rows={identifierMappings} emptyLabel="Not recorded" />
            <InlineMappings label={fieldMappingsLabel} rows={fieldMappings ?? []} />
          </>
        ) : null}
      </dl>
      {onEdit ? (
        <EditConnectionButton onEdit={onEdit} disabledReason={editDisabledReason} className="ml-auto shrink-0" />
      ) : null}
    </div>
  );
}

/**
 * Both bands' `Edit`. `keepMounted`: the reason clears once `workspace.get` lands, and an unwrapped flip
 * remounts the Button — a focused keyboard user would lose their place.
 */
function EditConnectionButton({
  onEdit,
  disabledReason,
  className,
}: {
  onEdit: () => void;
  disabledReason: string | undefined;
  className: string;
}): React.ReactElement {
  return (
    <ActionTooltip show={disabledReason !== undefined} message={disabledReason ?? ''} className={className} keepMounted>
      <Button
        type="button"
        variant="text"
        className="shrink-0 text-xs text-primary hover:text-primary hover:underline"
        label="Edit"
        aria-label="Edit connection settings"
        disabled={disabledReason !== undefined}
        onClick={onEdit}
      />
    </ActionTooltip>
  );
}
